import { Banner } from "@shopify/polaris";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";

import {
  inventoryReconciliationKeys,
  listInventoryReconciliations,
} from "../api/inventory_reconciliation";
import { t } from "../lib/i18n";

const NOTICE_REFRESH_MS = 60_000;

/**
 * 外部在庫書き込みの結果が不明な操作を、どの画面にいても見落とさせない。
 *
 * `UNKNOWN` だけでなく、照合後の Shopify 実在庫同期に失敗した
 * `sync_pending` も backend の未解決一覧に残る。取得自体が失敗したときも
 * バナーを消して「問題なし」に見せず、再試行の導線を残す。
 */
export function InventoryReconciliationNotice() {
  const navigate = useNavigate();
  const query = useQuery({
    queryKey: inventoryReconciliationKeys.unresolved,
    queryFn: () => listInventoryReconciliations(false),
    refetchInterval: NOTICE_REFRESH_MS,
  });

  if (query.isLoading) return null;

  if (query.isError) {
    return (
      <Banner
        tone="warning"
        title={t("在庫操作の確認状況を取得できませんでした")}
        action={{
          content: t("再試行"),
          onAction: () => void query.refetch(),
        }}
      >
        {t("Shopify への在庫反映結果が不明な操作が無いか確認できていません。通信環境を確認して再試行してください。")}
      </Banner>
    );
  }

  const operations = query.data?.operations ?? [];
  const count = operations.length;
  if (count === 0) return null;
  const onlyOperation = count === 1 ? operations[0] : null;

  return (
    <Banner
      tone="critical"
      title={t("在庫操作の確認が必要です")}
      action={{
        content:
          onlyOperation?.status === "sync_pending"
            ? t("在庫同期を再開")
            : onlyOperation !== null
              ? t("この在庫操作を確認")
              : t("{count} 件の在庫操作を確認", { count }),
        onAction: () =>
          navigate(
            onlyOperation === null
              ? "/inventory-reconciliation"
              : `/inventory-reconciliation/${onlyOperation.id}`,
          ),
      }}
    >
      {t("Shopify への反映結果を確認できなかった在庫操作が {count} 件あります。二重反映を防ぐため、次の在庫操作を行う前に確認してください。", {
        count,
      })}
    </Banner>
  );
}
